import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { DateTime } from "luxon"
import { useState } from "react"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { highlightColor } from "../../../../../../shared/colors/Colors"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { ExportButtonProductionList } from "../../components/ExportButtonProductionList"
import { SelectShop } from "../../components/SelectShop"
import { SelectWeek } from "../../components/SelectWeek"
import { fetchProductionListByPeriod } from "../../productionApi"
import { LoadingSpinner } from "../../../../../../shared/components/loading/LoadingSpinner"
import { StandardNoResultText } from "../../../../../../shared/components/noResult/StandardNoResultText"
import { useMerchant } from "../../../../../merchants/merchantHooks"
import { ShopsQuery, fetchShopsList } from "../../../../admin/shops/shopsApi"
import { useOrderTypeFilter } from "../../../../../../shared/components/filters/orderTypeFilter/useOrderTypeState"
import { StandardRefreshButton } from "../../../../../../shared/components/table/StandardRefreshButton"
import { RefetchIntervals } from "../../../../../../core/api/refetchIntervals"
import { ErrorDisplay } from "../../../../../../core/api/errorTypes"
import { makeStyles } from "tss-react/mui"

const useStyles = makeStyles()((theme) => ({
    accordion: {
        border: `1px solid ${theme.palette.tailwind.slate[100]}`,
        borderRadius: ".75rem !important",
        boxShadow: "0px 2px 6px 0px rgba(0, 0, 0, 0.08)",
        marginBottom: "1rem",
        "&::before": {
            display: "none",
        },
    },
}))

export type TableData = {
    productName: string
    productId: number
    datesObj: Record<DaysInWeek, DatesTableData>
    total: number
}

type DatesTableData = {
    displayDay: string
    date: string
    dayInWeek: number
    sale: {
        items: number
        amount: {
            amount: number
            currency: string
            scale: number
            formatted?: string
        }
    }
}

enum DaysInWeek {
    Mon = "Mon",
    Tue = "Tue",
    Wed = "Wed",
    Thu = "Thu",
    Fri = "Fri",
}

export function ProductionListTab() {
    const { merchantId } = useMerchant()
    const { classes } = useStyles()
    const [startDate, setStartDate] = useState(DateTime.now().startOf("week").toISODate())
    const [endDate, setEndDate] = useState(DateTime.now().endOf("week").toISODate())
    const [shopId, setShopId] = useState<string | undefined>("")
    const { orderType } = useOrderTypeFilter()

    const productionList = useApiQuery(fetchProductionListByPeriod, {
        queryName: "get-all-production-list-by-period",
        dependencies: {
            startDate: startDate,
            endDate: endDate,
            merchantId,
            shopId: shopId,
            orderType,
        },
        refetchInterval: RefetchIntervals.medium,
        errorDisplay: ErrorDisplay.NONE,
    })

    const allShops = useApiQuery(fetchShopsList, {
        queryName: ShopsQuery.LIST,
        dependencies: {
            merchantId,
        },
    })

    const today = DateTime.now().toISODate()

    // TODO Ask søren where to place this function
    const highlightToday = (date: string) => {
        if (date === today) {
            return highlightColor
        }

        return ""
    }

    return (
        <>
            {/*<StandardOrderTypeFilter*/}
            {/*    orderType={orderType}*/}
            {/*    setOrderType={setOrderType}*/}
            {/*    config={orderFilterButtonConfig}*/}
            {/*/>*/}

            <Grid container paddingY="2rem" alignItems="center" justifyContent={"space-between"}>
                <Grid item container width="auto" gap="2rem">
                    <SelectWeek setStartDate={setStartDate} startDate={startDate} setEndDate={setEndDate} />
                    <SelectShop shops={allShops.data?.shops ?? []} setShopId={setShopId} />
                </Grid>
                <Grid item container width="auto" alignItems="center" gap="1rem">
                    <StandardRefreshButton query={productionList} bordered labelPlacement="start" />
                    <ExportButtonProductionList query={productionList} startDate={startDate} />
                </Grid>
            </Grid>

            {productionList.isLoading && <LoadingSpinner />}
            {!productionList.data?.categories && !productionList.isFetching && <StandardNoResultText />}

            {productionList.data?.categories?.map((category) => (
                <Accordion className={classes.accordion} key={category.name} disableGutters defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography
                            sx={{ fontSize: "25px", color: "#64748B", fontWeight: "bold", paddingBottom: "0.5rem" }}
                        >
                            {category.name}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        {!category.products ? (
                            <LocalizedStrict id="production-list-no-result">
                                <Typography sx={{ fontSize: "14px", color: "#64748B", paddingBottom: "0.5rem" }}>
                                    No result
                                </Typography>
                            </LocalizedStrict>
                        ) : (
                            <TableContainer component={Paper} sx={{ marginBottom: "5rem" }}>
                                <Table sx={{ minWidth: 500 }}>
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: "#F8F8F8", textAlign: "center" }}>
                                            <TableCell sx={{ width: "auto" }}> </TableCell>
                                            {category.products[0]?.dates?.map((day) => (
                                                <TableCell
                                                    key={day.date}
                                                    sx={{
                                                        textAlign: "center",
                                                        backgroundColor: highlightToday(day.date),
                                                    }}
                                                >
                                                    {day.displayDay}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {category?.products?.map((product) => (
                                            <TableRow key={product.id}>
                                                <TableCell component="th" scope="row" sx={{ width: "250px" }}>
                                                    {product.name}
                                                </TableCell>
                                                {product?.dates?.map((date) => (
                                                    <TableCell
                                                        key={date.date}
                                                        sx={{
                                                            textAlign: "center",
                                                            width: "100px",
                                                            backgroundColor: highlightToday(date.date),
                                                        }}
                                                    >
                                                        {date.sale.items}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </AccordionDetails>
                </Accordion>
            ))}
        </>
    )
}
